/**
 * Pure preference math + persistence. No Web Audio here — this module is fully
 * unit-testable without a context. Bus gain is derived from prefs by pure
 * functions so both the live engine and the tests agree on the numbers.
 */
import type { KvStorePort } from '@djgg/core';
import type { AudioPrefs } from './types.js';

/** The KV key the engine reads/writes prefs under. */
export const AUDIO_PREFS_KEY = 'audio.prefs';

/** Platform defaults when nothing is persisted. */
export const DEFAULT_PREFS: AudioPrefs = Object.freeze({
  volume: 0.5,
  muted: false,
  musicEnabled: true,
  sfxEnabled: true,
});

/** Clamp a volume to the valid `0..1` range, coercing NaN/non-finite to 0. */
export function clampVolume(v: number): number {
  if (!Number.isFinite(v)) return 0;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

/**
 * Master-bus gain from prefs: muted ⇒ 0, else the clamped volume. The mute is a
 * hard gate that survives volume changes (setting volume while muted stores the
 * value but keeps the bus at 0).
 */
export function masterGainFor(prefs: AudioPrefs): number {
  return prefs.muted ? 0 : clampVolume(prefs.volume);
}

/** Music-bus gain: `musicVolume` when enabled, else 0. */
export function musicGainFor(prefs: AudioPrefs, musicVolume: number): number {
  return prefs.musicEnabled ? clampVolume(musicVolume) : 0;
}

/** SFX-bus gain: unity when enabled, else 0. */
export function sfxGainFor(prefs: AudioPrefs): number {
  return prefs.sfxEnabled ? 1 : 0;
}

/**
 * Coerce an arbitrary persisted value into a valid {@link AudioPrefs}, filling
 * missing/invalid fields from {@link DEFAULT_PREFS}. Tolerant by design: a
 * partial or corrupt blob must never throw at boot.
 */
export function normalizePrefs(raw: unknown): AudioPrefs {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Partial<Record<keyof AudioPrefs, unknown>>;
  return {
    volume: typeof o.volume === 'number' ? clampVolume(o.volume) : DEFAULT_PREFS.volume,
    muted: typeof o.muted === 'boolean' ? o.muted : DEFAULT_PREFS.muted,
    musicEnabled: typeof o.musicEnabled === 'boolean' ? o.musicEnabled : DEFAULT_PREFS.musicEnabled,
    sfxEnabled: typeof o.sfxEnabled === 'boolean' ? o.sfxEnabled : DEFAULT_PREFS.sfxEnabled,
  };
}

/** Load + normalize prefs from a KV store; returns defaults if absent/corrupt. */
export async function loadPrefs(kv: KvStorePort): Promise<AudioPrefs> {
  const raw = await kv.get<unknown>(AUDIO_PREFS_KEY);
  return normalizePrefs(raw);
}

/** Persist prefs to a KV store. */
export async function savePrefs(kv: KvStorePort, prefs: AudioPrefs): Promise<void> {
  await kv.set<AudioPrefs>(AUDIO_PREFS_KEY, prefs);
}
